import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Widget,
  dropMessages,
  getMessages,
  setMessages,
  setQuickButtons,
  toggleInputDisabled,
  toggleMsgLoader,
  toggleWidget,
} from './chat-widget/index';
import './styles.css';
import evieAvatar from './chat-widget/assets/evie-avatar.svg';
import { FeedbackPayload } from './chat-widget/src/components/Widget/components/Feedback';
import { ASKAI_SUBTITLE, ASKAI_TITLE, BOT_REPLY_DELAY_MS, END_CHAT_FEEDBACK_DELAY_MS, SHOW_TICKETS_TAB } from './config';
import { ConversationMessage, QuickButton } from './conversation/types';
import { LocalStorageConversationStore, removeLegacyKeys } from './conversation/storage';
import { createConversationService } from './conversation/conversationService';
import { toWidgetMessage } from './conversation/widgetAdapter';
import { CaseDetailsService, createCaseDetailsService } from './caseDetails/caseDetailsService';
import { resolvePractice } from './caseDetails/caseSummary';
import { createTriageService } from './triage/triageService';
import { createLlmService } from './llm/llmService';
import { createTicketService } from './ticket/ticketService';
import { createCaseMessageService } from './caseMessages/caseMessageService';
import { ChatEngine, createChatEngine, EngineResult } from './engine/chatEngine';

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Evie chat widget: a client-side, preset-driven assistant. Messages no preset matches are handed to the
 * LLM API (`REACT_APP_CHAT_LLM_END_POINT`); without one, Evie shows the canned fallback. Every conversation
 * is mirrored to a Jira ticket through `REACT_APP_CHAT_TICKET_END_POINT` (see engine/chatEngine.ts).
 *
 * All conversation logic lives in `engine/chatEngine.ts`; this component only wires the engine to the
 * vendored chat UI and keeps the widget's message list in sync with the persisted conversation.
 */
const AskAiChatWidget = () => {
  const userInfo = useSelector((state: any) => state.userInfo);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [sessionId, setSessionId] = useState<string>('rcw-chat-container');

  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  /** Set when the feedback form was reached by ending the chat, so the widget closes once it is done. */
  const closeAfterFeedbackRef = useRef(false);
  const feedbackTimerRef = useRef<number | null>(null);
  /** True while a turn is in flight; the composer and quick buttons are locked until Evie replies. */
  const busyRef = useRef(false);
  /** Mirrors the widget's toggle-only disabled-input flag. */
  const inputDisabledRef = useRef(false);
  /** Bumped when the session ends or is replaced, so a reply that lands afterwards is dropped. */
  const generationRef = useRef(0);

  const userKey = String(userInfo?.Email || userInfo?.email || 'anonymous').toLowerCase();
  const firstName = String(userInfo?.FirstName || '');

  const caseDetails: CaseDetailsService | null = useMemo(
    () => (userInfo ? createCaseDetailsService(userInfo) : null),
    // Rebuilt only when the signed-in user changes, so its case cache survives re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userKey]
  );

  const engine: ChatEngine | null = useMemo(() => {
    if (!userInfo || !caseDetails) return null;
    const practice = resolvePractice(userInfo);
    const conversation = createConversationService(new LocalStorageConversationStore(), userKey, practice?.practiceId);
    return createChatEngine({
      conversation,
      caseDetails,
      triage: createTriageService(),
      llm: createLlmService(),
      ticket: createTicketService(),
      caseMessages: createCaseMessageService(String(userInfo?.Id || '')),
      user: {
        firstName,
        email: String(userInfo?.Email || ''),
        practiceId: practice?.practiceId,
        personnelIds: practice?.personnelIds || [],
      },
    });
    // The engine is rebuilt only when the signed-in user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  const renderAll = useCallback((messages: ConversationMessage[], quickButtons: QuickButton[]) => {
    setMessages(messages.map((m) => toWidgetMessage(m, false)));
    setQuickButtons(quickButtons);
  }, []);

  const closeWidget = useCallback(() => {
    if (!isOpenRef.current) return;
    toggleWidget();
    setIsOpen(false);
  }, []);

  /** Ends the chat: the feedback form appears (after `delayMs`, so a goodbye bubble can be read) and the widget closes once it is submitted or skipped. */
  const beginEndChat = useCallback((delayMs: number) => {
    closeAfterFeedbackRef.current = true;
    if (feedbackTimerRef.current !== null) window.clearTimeout(feedbackTimerRef.current);
    if (delayMs <= 0) {
      setShowFeedback(true);
      return;
    }
    feedbackTimerRef.current = window.setTimeout(() => {
      feedbackTimerRef.current = null;
      setShowFeedback(true);
    }, delayMs);
  }, []);

  useEffect(() => () => {
    if (feedbackTimerRef.current !== null) window.clearTimeout(feedbackTimerRef.current);
  }, []);

  const setInputDisabled = useCallback((disabled: boolean) => {
    if (inputDisabledRef.current === disabled) return;
    toggleInputDisabled();
    inputDisabledRef.current = disabled;
  }, []);

  const renderResult = useCallback((result: EngineResult) => {
    const unread = !isOpenRef.current;
    const current = getMessages();
    const known = new Set(current.map((m) => m.customId));
    const additions = result.newMessages
      .filter((m) => !known.has(m.id))
      .map((m) => toWidgetMessage(m, unread && m.role === 'bot'));
    if (additions.length > 0) setMessages([...current, ...additions]);
    setQuickButtons(result.quickButtons);
    if (result.endChat) beginEndChat(END_CHAT_FEEDBACK_DELAY_MS);
  }, [beginEndChat]);

  // Load (or start) the conversation whenever the engine changes.
  useEffect(() => {
    if (!engine) return;
    let cancelled = false;
    generationRef.current += 1;
    removeLegacyKeys();
    setIsLoading(true);

    engine
      .start()
      .then(({ messages, quickButtons }) => {
        if (cancelled) return;
        renderAll(messages, quickButtons);
        setSessionId(engine ? `evie-${userKey}` : 'rcw-chat-container');
      })
      .catch((error) => console.error('[askAi] could not start conversation', error))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [engine, renderAll, userKey]);

  // One turn at a time: the user's bubble shows straight away, the composer and quick buttons lock,
  // and both come back once Evie replies (the echo is then swapped for the persisted copy).
  const runWithEcho = useCallback(
    async (echoText: string, action: () => Promise<EngineResult>) => {
      if (busyRef.current) return;
      busyRef.current = true;
      const generation = generationRef.current;
      const echoId = `local-${Date.now()}`;
      setMessages([...getMessages(), toWidgetMessage({ id: echoId, role: 'user', text: echoText, createdAt: Date.now() }, false)]);
      setQuickButtons([]);
      setInputDisabled(true);
      let loaderOn = false;
      try {
        if (isOpenRef.current) {
          toggleMsgLoader();
          loaderOn = true;
        }
        const [result] = await Promise.all([action(), wait(BOT_REPLY_DELAY_MS)]);
        if (loaderOn) {
          toggleMsgLoader();
          loaderOn = false;
        }
        setMessages(getMessages().filter((m) => m.customId !== echoId));
        // A reply that lands after the session was ended or replaced is dropped.
        if (generation === generationRef.current) renderResult(result);
      } catch (error) {
        console.error('[askAi] turn failed', error);
        setMessages(getMessages().filter((m) => m.customId !== echoId));
        if (loaderOn) {
          try { toggleMsgLoader(); } catch {}
        }
      } finally {
        busyRef.current = false;
        setInputDisabled(false);
      }
    },
    [renderResult, setInputDisabled]
  );

  const handleNewUserMessage = useCallback(
    (text: string) => {
      const trimmed = String(text ?? '').trim();
      if (!trimmed || !engine) return;
      void runWithEcho(trimmed, () => engine.handleUserText(trimmed));
    },
    [engine, runWithEcho]
  );

  const handleQuickButtonClicked = useCallback(
    (value: string | number) => {
      if (!engine) return;
      const raw = String(value);
      void runWithEcho(engine.quickButtonText(raw), () => engine.handleQuickButton(raw));
    },
    [engine, runWithEcho]
  );

  const handleStartNewSession = useCallback(() => {
    closeAfterFeedbackRef.current = false;
    setShowFeedback(true);
  }, []);

  const handleEndChat = useCallback(() => {
    beginEndChat(0);
  }, [beginEndChat]);

  /** Archives the current session (with feedback when given), greets afresh, and closes the widget if the chat was ended. */
  const finishSession = useCallback(
    async (feedback?: { resolved: boolean; score: number; comment: string }) => {
      if (!engine) return;
      const shouldClose = closeAfterFeedbackRef.current;
      generationRef.current += 1;
      try {
        const { messages, quickButtons } = await engine.newSession(feedback);
        dropMessages();
        renderAll(messages, quickButtons);
      } catch (error) {
        console.error('[askAi] could not start a new session', error);
      } finally {
        closeAfterFeedbackRef.current = false;
        setShowFeedback(false);
        if (shouldClose) closeWidget();
      }
    },
    [engine, renderAll, closeWidget]
  );

  const onSubmitFeedback = useCallback(
    (feedback: FeedbackPayload) =>
      finishSession({
        resolved: feedback.resolved,
        score: feedback.resolved ? feedback.score : 0,
        comment: feedback.comment,
      }),
    [finishSession]
  );

  const onSkipFeedback = useCallback(() => finishSession(), [finishSession]);

  const onMessageFeedback = useCallback(
    (messageId: string, rating: 'liked' | 'disliked') => {
      void engine?.rateMessage(messageId, rating);
    },
    [engine]
  );

  // Reopening the widget starts from fresh case data; while it stays open, lookups reuse the cache.
  const openWidget = (opened: boolean) => {
    if (opened && !isOpenRef.current) caseDetails?.invalidate();
    setIsOpen(opened);
  };

  const toggleChatWindow = () => {
    toggleWidget();
    openWidget(!isOpenRef.current);
  };

  if (!engine) return null;

  return (
    <div style={{ textAlign: 'center' }}>
      <button
        className={`askai-toggle-overlay${isOpen ? ' is-open' : ''}`}
        onClick={toggleChatWindow}
        aria-label="Toggle Evie chat"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '0',
          cursor: 'pointer',
          zIndex: 10000000,
          borderRadius: '50%',
          width: '60px',
          height: '60px',
        }}
      />

      <Widget
        handleNewUserMessage={handleNewUserMessage}
        handleQuickButtonClicked={handleQuickButtonClicked}
        handleStartNewSession={handleStartNewSession}
        handleEndChat={handleEndChat}
        handleToggle={openWidget}
        title={ASKAI_TITLE}
        subtitle={ASKAI_SUBTITLE}
        profileAvatar={evieAvatar}
        isOpen={isOpen}
        chatId={sessionId}
        emojis={true}
        showTimeStamp={false}
        showBadge={false}
        showCloseButton={true}
        isLoading={isLoading && !isOpen}
        showFeedback={showFeedback}
        onSubmitFeedback={onSubmitFeedback}
        onSkipFeedback={onSkipFeedback}
        onMessageFeedback={onMessageFeedback}
        showTicketsTab={SHOW_TICKETS_TAB}
      />
    </div>
  );
};

export default AskAiChatWidget;
